/**
 * Dashboard-specific goal type identifiers.
 * These are stored as `goal_type` on Goal model records created via the TeamGoalsModal.
 *
 * `dashboard_save` was retired in PAN-520: an 80% renewal goal already implies a
 * 20% loss goal by definition, so keeping Save as an independent bucket double-counts
 * Renewal. Existing rows in the DB are harmless — `getDashboardGoals` only surfaces
 * the types in DASHBOARD_GOAL_TYPE_LIST.
 */
export const DASHBOARD_GOAL_TYPES = {
  retention: "dashboard_retention",
  expansion: "dashboard_expansion",
  pilot: "dashboard_pilot",
};

export const DASHBOARD_GOAL_TYPE_LIST = Object.values(DASHBOARD_GOAL_TYPES);

/**
 * Default target percentages when no goals have been created yet.
 * Pilot defaults to 100% — matches the widget's pre-PAN-520 behavior of treating
 * the full in-play pool as the target when no per-team goal is set.
 */
export const GOAL_DEFAULTS = {
  [DASHBOARD_GOAL_TYPES.retention]: 92,
  [DASHBOARD_GOAL_TYPES.expansion]: 65,
  [DASHBOARD_GOAL_TYPES.pilot]: 100,
};

/**
 * Get the current fiscal quarter label (e.g. "Q1 2026") for display.
 */
export const getQuarterLabel = () => {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `Q${quarter} ${now.getFullYear()}`;
};

/**
 * Current fiscal quarter label in the backend's canonical "YYYY-QN" shape (PAN-568).
 *
 * The API's ``quarter_label`` field uses this format. When the widget commits a leader
 * goal via ``/metrics/team-quarter-goal/``, the ``quarter_label`` body param must match
 * what ``team_fiscal_quarter(team).label`` returns server-side — see
 * ``apps.pano.accounts.domain.quarter`` in pano-api.
 *
 * Prefer the API's echoed ``quarter_label`` when available (accounts for team fiscal
 * year offsets); fall back to this calendar-year computation only when no API response
 * is in hand.
 */
export const getBackendQuarterLabel = () => {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  return `${now.getFullYear()}-Q${quarter}`;
};

/**
 * A local calendar date as YYYY-MM-DD.
 *
 * Not `toISOString()`: that converts local midnight to UTC, which is the previous day
 * for every browser east of UTC (PAN-718 — a Copenhagen user's quarter ended Dec 30).
 */
export const toCalendarDateString = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * Get the end date of the current fiscal quarter (YYYY-MM-DD).
 */
export const getQuarterEndDate = () => {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  const endMonth = quarter * 3;
  return toCalendarDateString(new Date(now.getFullYear(), endMonth, 0));
};

/**
 * Convert a `historical_data` array into SVG path strings for an inline
 * sparkline. Data arrives with period 0 = most recent; we sort so the oldest
 * point is on the left and the newest on the right.
 *
 * Shared by the dashboard metric cards (Portfolio Health, Pilots) so the two
 * surfaces draw sparklines identically.
 *
 * @param {Array<{period: number, value: number}>} historicalData
 * @returns {{linePath: string, areaPath: string, endPoint: ({x:number,y:number}|null)}|null}
 */
export const buildSparklinePaths = (
  historicalData,
  width = 200,
  height = 32,
) => {
  if (!historicalData || historicalData.length === 0) return null;

  // A quarter with no data comes back as null (PAN-819): a gap, not a 0. The points around it
  // keep their own slots, the line breaks across it, and the end dot (today) is drawn only when
  // the newest slot has a value.
  const sorted = [...historicalData].sort((a, b) => b.period - a.period);
  const hasValue = (d) => d.value !== null && d.value !== undefined;
  const real = sorted.filter(hasValue);
  if (real.length === 0) return null;
  const values = real.map((d) => d.value);

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const padding = 4;
  const at = (i, value) => ({
    x: sorted.length === 1 ? width / 2 : (i / (sorted.length - 1)) * width,
    y: padding + ((max - value) / range) * (height - 2 * padding),
  });

  if (real.length === sorted.length) {
    // No gaps: exactly the path this function always drew.
    const points = sorted.map((d, i) => at(i, d.value));
    const linePath = `M${points.map((p) => `${p.x},${p.y}`).join(" L")}`;
    return {
      linePath,
      areaPath: `${linePath} V${height} H0 Z`,
      endPoint: points[points.length - 1],
    };
  }

  // Runs of consecutive quarters with a value, each drawn as its own segment.
  const runs = [];
  sorted.forEach((d, i) => {
    if (!hasValue(d)) return;
    const point = at(i, d.value);
    const last = runs[runs.length - 1];
    if (last && last.end === i - 1) {
      last.points.push(point);
      last.end = i;
    } else runs.push({ end: i, points: [point] });
  });
  const segment = (pts) => `M${pts.map((p) => `${p.x},${p.y}`).join(" L")}`;
  const linePath = runs.map((r) => segment(r.points)).join(" ");
  const areaPath = runs
    .map((r) => `${segment(r.points)} V${height} H${r.points[0].x} Z`)
    .join(" ");
  const lastRun = runs[runs.length - 1];
  const endPoint =
    lastRun.end === sorted.length - 1
      ? lastRun.points[lastRun.points.length - 1]
      : null;

  return { linePath, areaPath, endPoint };
};

/**
 * `buildSparklinePaths`, except that a series of quarters (PAN-819) with under two real points
 * (a client's first quarters, or a tile shown as —) draws nothing: one point would render as a
 * lone wedge that reads as a trend. Every dashboard sparkline goes through this.
 *
 * Quarters before a client's data starts are dropped too (PAN-876), so the line spans the
 * whole tile instead of starting part-way across it. A gap after the first real quarter stays.
 */
export const buildDashboardSparkline = (historicalData) => {
  const real = (historicalData || []).filter((p) => p?.value != null);
  if (real.length < 2) return null;
  const firstPeriod = Math.max(...real.map((p) => p.period));
  return buildSparklinePaths(
    historicalData.filter((p) => p.period <= firstPeriod),
  );
};

/**
 * Format a number in compact form (e.g. 1200000 → "1.2M", 48000 → "48K").
 *
 * Unlike `formatCurrency` from currencyUtils (which includes symbol and uses
 * ".toFixed(1)" for K values → "€48.0K"), this produces cleaner labels for
 * dashboard cards/goals. Callers prepend the currency symbol separately.
 */
export const formatCompact = (value) => {
  if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `${Math.round(value / 1000)}K`;
  return `${Math.round(value)}`;
};

/**
 * Goal-surface variant of formatCompact. ``precise`` keeps one decimal on
 * thousands ("1.3K" rather than "1K"), where whole thousands made a small
 * book's goal misleading ($1,300 of $1,564 read as "$1K of $2K"). Not precise:
 * exactly formatCompact.
 */
export const formatGoalCompact = (value, precise) => {
  if (!precise) return formatCompact(value);
  // Thresholds on the rounded value, so 999.6 reads "1.0K" and 999,950 "1.0M".
  if (value >= 999950) return `${(value / 1000000).toFixed(1)}M`;
  if (value >= 999.5) return `${(value / 1000).toFixed(1)}K`;
  return `${Math.round(value)}`;
};

/**
 * One decimal on thousands and millions with a trailing ".0" dropped
 * (1500 → "1.5K", 15000 → "15K", 47600 → "47.6K"). The priority row and the
 * account page both use it, so one amount prints one string on both (PAN-910:
 * a €1,500 pilot read "€2K" on the dashboard and "€1.5K" on its account page).
 */
export const formatCompactOneDecimal = (value) => {
  const abs = Math.abs(value);
  const oneDecimal = (n) => n.toFixed(1).replace(/\.0$/, "");
  // Thresholds on the rounded value, so 999.6 reads "1K" and 999,950 "1M".
  if (abs >= 999950) return `${oneDecimal(value / 1000000)}M`;
  if (abs >= 999.5) return `${oneDecimal(value / 1000)}K`;
  return `${Math.round(value)}`;
};

/**
 * Safely parse a money amount from a summary bucket.
 * @param {object|null} summary - Dashboard summary response
 * @param {string} key - Bucket key ("renewal", "expansion", "at_risk")
 * @returns {number}
 */
export const getMrrAmount = (summary, key) => {
  return parseFloat(summary?.[key]?.total_mrr?.amount ?? 0);
};

/**
 * Get account count from a summary bucket.
 */
export const getCount = (summary, key) => {
  return summary?.[key]?.count ?? 0;
};

/**
 * Extract currency code from any summary bucket, falling back to "EUR".
 * Single source of truth — every component should use this instead of
 * duplicating `summary?.renewal?.total_mrr?.currency ?? "EUR"`.
 */
export const getSummaryCurrency = (summary) => {
  return (
    summary?.renewal?.total_mrr?.currency ??
    summary?.at_risk?.total_mrr?.currency ??
    summary?.expansion?.total_mrr?.currency ??
    "EUR"
  );
};

/**
 * Pilot pool: in-play + already-converted MRR for the current quarter.
 * Used by `GoalBreakdownModal` (the "Pool: €X" card body), by
 * `computeGoalTargets` as its no-goal fallback, and by `computeEditingTargets`
 * as its legacy base. `TeamGoalsModal`'s row shows the goal's base instead
 * (PAN-638), which counts in-play pilots only.
 *
 * @param {object|null} nrrProgress - NRR-goal-progress response
 * @returns {number} pool in team currency
 */
export const getPilotPool = (nrrProgress) => {
  if (!nrrProgress) return 0;
  return (
    Number(nrrProgress?.pilots?.pipeline ?? 0) +
    Number(nrrProgress?.pilots?.amount ?? 0)
  );
};

/**
 * Compute goal targets — three-bucket additive shape (PAN-520 + PAN-568).
 *
 * PAN-568 flipped the target denominator from a standing customer pool to a per-
 * quarter frozen commitment. When the API returns a materialized ``goal`` sub-key
 * for a bucket (backend Phases 1-5), we read ``amount_mrr`` directly — that is
 * the goal the leader (or resolver) committed to at first observation of the
 * quarter, and the ring's denominator is honest to the pipeline it will be
 * measured against.
 *
 * When ``goal`` is null (backend feature-flag off, dormant-team cap fired,
 * zero-flag Expansion team, sub-dollar floor), we fall back to the legacy
 * pool-×-% math. That fallback preserves the pre-PAN-568 rendering for teams
 * we haven't rolled out to yet.
 *
 * Consumed by TeamGoalsModal, GoalBreakdownModal, and RevenueGoalCard.
 *
 * @param {object|null} summary - Dashboard summary response
 * @param {object|null} nrrProgress - NRR-goal-progress response
 * @param {{ retention: number, expansion: number, pilot: number }} pcts - Target pcts (0-100)
 * @returns {{ target, renewalTarget, expansionTarget, pilotTarget, pilotsMapped }}
 */
const _numberFromGoalKey = (nrrProgress, bucket, key) => {
  const raw = nrrProgress?.[bucket]?.goal?.[key];
  if (raw === undefined || raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * The resolver's authoritative base for a bucket (``ResolvedGoal.base_mrr``),
 * serialised as ``nrrProgress.<bucket>.goal.base_mrr_at_set``.
 *
 * Returns ``null`` when no ``goal`` sub-key is present (unenrolled team,
 * dormant cap, sub-dollar floor) — caller should fall back to the legacy
 * ``summary.<bucket>`` value.
 *
 * PAN-587: the modal formula string must be composed from THIS base, not
 * from ``summary.<bucket>.total_mrr``. Those two are legitimately different
 * cohorts and diverge (Renewal hard-diverges today; Pilots after the first
 * mid-quarter conversion; Expansion only when the leader's committed pct
 * differs from the current slider).
 */
export const getFrozenBase = (bucket, nrrProgress) =>
  _numberFromGoalKey(nrrProgress, bucket, "base_mrr_at_set");

/**
 * The pct the frozen goal was committed at (``ResolvedGoal.pct``), serialised
 * as ``nrrProgress.<bucket>.goal.pct_at_set``.
 *
 * Returns ``null`` when the leader typed a fixed-dollar target directly
 * (no rate), or when the goal sub-key is absent. Callers rendering a
 * ``base × pct = target`` formula must drop the formula when this is null,
 * because the amount was not derived from a rate.
 */
export const getFrozenPct = (bucket, nrrProgress) =>
  _numberFromGoalKey(nrrProgress, bucket, "pct_at_set");

/**
 * PAN-796: whether the backend's pilots conversion goal is on for this team —
 * it sends ``pilots.live_base_mrr`` (the quarter's PoC cohort) only then.
 */
export const isPilotsConversionOn = (nrrProgress) =>
  Boolean(nrrProgress?.pilots) && "live_base_mrr" in nrrProgress.pilots;

export const computeGoalTargets = (summary, nrrProgress, pcts) => {
  const frozenAmount = (bucket) =>
    _numberFromGoalKey(nrrProgress, bucket, "amount_mrr");

  // PAN-587: a bucket is "estimated" when the API says so — a live compute at
  // read time, not a leader-committed row. Legacy pool-×-% fallback also reads
  // as estimated (no frozen row backs it either).
  const isEstimated = (bucket, usedFrozen) =>
    usedFrozen ? Boolean(nrrProgress?.[bucket]?.estimated) : true;

  const legacyRenewal = summary
    ? getMrrAmount(summary, "renewal") * (pcts.retention / 100)
    : 0;
  const legacyExpansion = summary
    ? getMrrAmount(summary, "expansion") * (pcts.expansion / 100)
    : 0;

  const pilotsMapped = Boolean(nrrProgress?.pilots_mapped);
  const pilotPool = getPilotPool(nrrProgress);
  const legacyPilot = pilotsMapped ? pilotPool * (pcts.pilot / 100) : 0;

  // Frozen goal takes precedence when present; legacy pool-×-% is the fallback
  // for teams the backend hasn't materialized (feature flag off, dormant cap,
  // no eligible accounts). The three tiers converge on `target` at the end.
  const frozenRenewal = frozenAmount("renewal");
  const frozenExpansion = frozenAmount("expansion");
  const frozenPilots = frozenAmount("pilots");
  const renewalTarget = frozenRenewal ?? legacyRenewal;
  const expansionTarget = frozenExpansion ?? legacyExpansion;
  // PAN-796: with the conversion goal on (the backend sends
  // pilots.live_base_mrr), no pilots goal means none this quarter — no pilots
  // end in it, or the cohort is under the floor. Never fall back to the legacy
  // pool × saved %, a target the backend does not have.
  const pilotsConversionOn = isPilotsConversionOn(nrrProgress);
  const pilotTarget = !pilotsMapped
    ? 0
    : (frozenPilots ?? (pilotsConversionOn ? 0 : legacyPilot));

  return {
    target: renewalTarget + expansionTarget + pilotTarget,
    renewalTarget,
    expansionTarget,
    pilotTarget,
    pilotsMapped,
    estimated: {
      renewal: isEstimated("renewal", frozenRenewal !== null),
      expansion: isEstimated("expansion", frozenExpansion !== null),
      pilots:
        pilotsMapped && !(pilotsConversionOn && frozenPilots === null)
          ? isEstimated("pilots", frozenPilots !== null)
          : false,
    },
  };
};

const _round2 = (v) => Math.round(v * 100) / 100;

const _finiteOrNull = (raw) => {
  if (raw === undefined || raw === null) return null;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Editing targets for the Team Goals modal: ``base × slider`` per bucket (PAN-587).
 *
 * ``computeGoalTargets`` is deliberately NOT used for editing: it pins the
 * committed ``goal.amount_mrr``, so a slider change would never move the preview
 * and a stale committed amount would be re-saved verbatim. Here the base is the
 * one the server stamps on commit, and the target is recomputed from the slider.
 *
 * Base rules, per bucket:
 * - ``nrrProgress`` null, or the bucket has no ``goal`` key (older backend) →
 *   legacy pool: ``summary.<renewal|expansion>.total_mrr`` or ``getPilotPool``.
 * - Renewal: ``live_base_mrr`` first when present (PAN-639, sent only with
 *   renewal_goal_frozen on), then as pilots.
 * - Pilots: ``live_base_mrr`` first when present (PAN-796: the quarter's whole
 *   PoC cohort, sent only when the backend's pilots conversion goal is on —
 *   estimated_goal_pill, a January fiscal year and a deal-mapped end date).
 * - Renewal / pilots: ``goal.base_mrr_at_set`` when the goal is non-null;
 *   ``goal: null`` means the base is below the floor → ``0``.
 * - Expansion: ``live_base_mrr`` (the resolver's live expansion base — what the
 *   server stamps on commit) when finite; else ``goal.base_mrr_at_set``; else the
 *   legacy summary pool. ``live_base_mrr`` is absent on older backends. With the
 *   ``estimated_goal_pill`` flag on it is upsells in play PLUS upsells already won
 *   this quarter (the converted part), plus renewal increases (open and closed);
 *   with the flag off it is still the account book.
 * - Pilots: ``0`` when ``pilots_mapped`` is false.
 *
 * Target: ``round2(base × pct / 100)``.
 *
 * @param {object|null} summary - Dashboard summary response (team-wide)
 * @param {object|null} nrrProgress - NRR-goal-progress response (team-wide)
 * @param {{ retention: number, expansion: number, pilot: number }} pcts - Slider pcts (0-100)
 * @returns {{ renewal: {base, target}, expansion: {base, target}, pilots: {base, target}, total: number, pilotsMapped: boolean }}
 */
export const computeEditingTargets = (summary, nrrProgress, pcts) => {
  const pilotsMapped = Boolean(nrrProgress?.pilots_mapped);

  const hasGoalKey = (bucket) => {
    const obj = nrrProgress?.[bucket];
    return Boolean(obj) && typeof obj === "object" && "goal" in obj;
  };

  const legacyBase = (bucket) =>
    bucket === "pilots"
      ? getPilotPool(nrrProgress)
      : getMrrAmount(summary, bucket);

  const baseFor = (bucket) => {
    if (!nrrProgress || !hasGoalKey(bucket)) return legacyBase(bucket);
    const goalBase = _finiteOrNull(nrrProgress[bucket].goal?.base_mrr_at_set);
    if (bucket === "expansion") {
      const live = _finiteOrNull(nrrProgress.expansion.live_base_mrr);
      if (live !== null) return live;
      if (goalBase !== null) return goalBase;
      return legacyBase(bucket);
    }
    // PAN-639: the backend sends renewal ``live_base_mrr`` only with
    // renewal_goal_frozen on; the modal then previews against the live base,
    // not the base frozen when the goal was set.
    // PAN-796: pilots likewise — live_base_mrr is the quarter's whole PoC
    // cohort (in play, won and lost), the base the conversion rate applies to.
    if (bucket === "renewal" || bucket === "pilots") {
      const live = _finiteOrNull(nrrProgress[bucket].live_base_mrr);
      if (live !== null) return live;
    }
    // Renewal / pilots: a null goal means the base is below the floor.
    return goalBase ?? 0;
  };

  const renewalBase = baseFor("renewal");
  const expansionBase = baseFor("expansion");
  const pilotsBase = pilotsMapped ? baseFor("pilots") : 0;

  const renewal = {
    base: renewalBase,
    target: _round2((renewalBase * pcts.retention) / 100),
  };
  const expansion = {
    base: expansionBase,
    target: _round2((expansionBase * pcts.expansion) / 100),
  };
  const pilots = {
    base: pilotsBase,
    target: _round2((pilotsBase * pcts.pilot) / 100),
  };

  return {
    renewal,
    expansion,
    pilots,
    total: renewal.target + expansion.target + pilots.target,
    pilotsMapped,
  };
};

/**
 * Start of the current fiscal quarter as YYYY-MM-DD.
 */
export const getQuarterStartDate = () => {
  const now = new Date();
  const quarter = Math.ceil((now.getMonth() + 1) / 3);
  const startMonth = (quarter - 1) * 3;
  return toCalendarDateString(new Date(now.getFullYear(), startMonth, 1));
};

/**
 * Find existing dashboard goals from a goals array, keyed by goal_type.
 * Only returns goals whose due_date is in the current quarter or later —
 * expired goals from previous quarters are excluded so the widget prompts
 * the user to set new goals instead of computing stale targets.
 */
export const getDashboardGoals = (goals) => {
  const quarterStart = getQuarterStartDate();
  const byType = {};
  for (const g of goals) {
    if (
      DASHBOARD_GOAL_TYPE_LIST.includes(g.goal_type) &&
      g.due_date >= quarterStart
    ) {
      byType[g.goal_type] = g;
    }
  }
  return byType;
};

/**
 * Get goal target percentage, falling back to default if goal doesn't exist.
 *
 * For monetary goals created via the TeamGoalsModal, the slider percentage is
 * stored in the `description` field as JSON `{"target_pct": N}`.
 * Legacy percentage goals still use `numerical_target`.
 */
export const getTargetPct = (goalsByType, goalType) => {
  const goal = goalsByType[goalType];
  if (!goal) return GOAL_DEFAULTS[goalType] ?? 0;

  // Monetary goals: read percentage from description JSON
  if (goal.goal_category === "monetary" && goal.description) {
    try {
      const parsed = JSON.parse(goal.description);
      if (parsed.target_pct != null) return parsed.target_pct;
    } catch (e) {
      // Fall through to numerical_target
    }
  }

  return goal.numerical_target ?? GOAL_DEFAULTS[goalType] ?? 0;
};
